import {Directive, forwardRef, Input, OnChanges, OnInit} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Observable} from 'rxjs';
import {createDisplaySymbol} from '../../common/display-symbol';
import {TypedSimpleChanges} from '../../common/typed-on-changes';
import {Security} from '../../models/security';
import {
    AutocompleteConfig,
    AutocompleteDirective,
    AutocompleteProviderConfig,
    AutocompleteSearchType
} from '../autocomplete';
import {SecurityAutocompleteListComponent} from './security-autocomplete-list.component';
import {SecurityAutocompleteItem, SecurityAutocompleteService} from './security-autocomplete.service';

@Directive({
    selector: '[securityAutocomplete]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SecurityAutocompleteDirective),
            multi: true
        }
    ],
    standalone: false
})
export class SecurityAutocompleteDirective extends AutocompleteDirective<Security>
    implements ControlValueAccessor, OnChanges, OnInit {

    @Input('securityAutocomplete')
    public inputConfig: AutocompleteConfig;

    public override listComponent: SecurityAutocompleteListComponent;

    protected override readonly listComponentClass = SecurityAutocompleteListComponent;

    protected readonly securityAutocompleteProviderService = this.injector.get(SecurityAutocompleteService);

    private autocompleteItems: SecurityAutocompleteItem[];

    public ngOnChanges(changes: TypedSimpleChanges<SecurityAutocompleteDirective>): void {
        if (changes.inputConfig && !changes.inputConfig.firstChange) {
            this.updateConfig();
        }
    }

    public override ngOnInit(): void {
        this.updateConfig();

        super.ngOnInit();
    }

    protected override getRawVariantsByTerm(term: string): Observable<SecurityAutocompleteItem[]> {
        return this.securityAutocompleteProviderService.autocomplete(
            term,
            this.config.type as AutocompleteProviderConfig<any>
        );
    }

    protected override transformRawVariants(items: SecurityAutocompleteItem[]): Security[] {
        this.autocompleteItems = items; // Store the raw autocomplete items in order to reuse it in the list component.

        return items && items.map(item => item.security);
    }

    protected override open(): void {
        super.open();

        this.listComponent.term$ = this.term$;
    }

    protected override setListComponentVariants(): void {
        this.listComponent.setVariants(this.variants, this.autocompleteItems);
    }

    private updateConfig(): void {
        const inputConfig = this.inputConfig || {};
        const inputConfigType: AutocompleteSearchType<Security> = inputConfig.type;
        const filters = inputConfigType && (inputConfigType as AutocompleteProviderConfig<Security>).filters
            || undefined;

        this.config = {
            freeTyping:                false,
            disallowEmptySelect:       true,
            selectAllOnFocus:          true,
            allowVariantsForEmptyTerm: true,

            ...inputConfig,

            type: {
                filters,
                provider: this.securityAutocompleteProviderService
            },

            displayValue: (security: Security) => createDisplaySymbol(security).displayStr
        };
    }

}
